import { AxiosInstance, AxiosRequestConfig } from "axios";
import { doRefreshToken } from "utils/authen";
import { logDev } from "utils/logs";

interface QueueRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}

export class RefreshTokenHandler {
  private _isRefreshing = false;
  private failedQueue: QueueRequest[] = [];

  public get isRefreshing() {
    return this._isRefreshing;
  }

  public async handleAuthError(
    config: AxiosRequestConfig,
    http: AxiosInstance
  ): Promise<any> {
    if (!this._isRefreshing) {
      return this.handleRefreshToken(config, http);
    }

    return this.queueFailedRequest(config, http);
  }

  private async handleRefreshToken(
    originalRequest: AxiosRequestConfig,
    http: AxiosInstance
  ): Promise<any> {
    this._isRefreshing = true;

    try {
      const isLogged = await doRefreshToken();
      if (!isLogged) {
        this.processQueue(new Error("Refresh token failed"));

        return Promise.reject(new Error("Authentication failed"));
      }

      // Mark request as retried
      this.processQueue();

      // re-execute request
      return http(originalRequest);
    } catch (refreshError) {
      this.processQueue(refreshError);
      logDev({ refreshError });

      return Promise.reject(refreshError);
    } finally {
      this._isRefreshing = false;
    }
  }

  public addQueue(originalRequest: AxiosRequestConfig, http: AxiosInstance) {
    return this.queueFailedRequest(originalRequest, http);
  }

  // Add request to queue for handle later after refresh token success
  private queueFailedRequest(
    originalRequest: AxiosRequestConfig,
    http: AxiosInstance
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.failedQueue.push({
        resolve: () => {
          resolve(http(originalRequest));
        },
        reject,
      });
    });
  }

  // Handle request in queue
  private processQueue(error: any = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });

    // Reset queue after done
    this.failedQueue = [];
  }
}
